import React from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { productService } from '../../services/api';
import { formatCurrency } from '../../utils/formatters';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e0e0e0;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;

  &:hover {
    color: #333;
  }
`;

const EditForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #555;
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const TextArea = styled.textarea`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  min-height: 80px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const ComponentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const ComponentItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  border-left: 3px solid #007bff;
`;

const ComponentInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const ComponentName = styled.span`
  font-weight: 500;
  color: #333;
  font-size: 0.95rem;
`;

const ComponentType = styled.span`
  color: #999;
  font-size: 0.8rem;
  margin-top: 0.25rem;
`;

const ComponentPrice = styled.span`
  color: #666;
  font-size: 0.9rem;
  font-weight: 500;
`;

const Select = styled.select`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.875rem;
  background: white;
  cursor: pointer;
  width: 100%;
  margin-top: 0.25rem;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const ComponentActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-end;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #e0e0e0;
`;

const SaveButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background-color: #218838;
  }

  &:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background-color: #5a6268;
  }
`;

const SummarySection = styled.div`
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  font-size: 0.95rem;

  &:last-child {
    margin-bottom: 0;
    padding-top: 0.5rem;
    border-top: 1px solid #dee2e6;
    font-weight: 600;
    font-size: 1.1rem;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.9rem;
  color: #555;
`;

const PriceInput = styled(Input)`
  width: 120px;
  text-align: right;
  font-weight: 500;

  &.manual {
    background-color: #fff8e1;
    border-color: #ff9800;
  }
`;

const AddComponentSection = styled.div`
  background-color: #e8f5e9;
  padding: 1rem;
  border-radius: 4px;
  border: 1px dashed #4caf50;
`;

const EditLineModal = ({ lineData, onClose, onSave, showPriceWithVAT, mode = 'edit', articleFingerprint, order }) => {
  // mode: 'edit' = edit existing line(s), 'add' = add new line to article
  const isAddMode = mode === 'add';

  // Detect if this is a TPV order - uses same logic as backend/CalculatorPro
  const isTPVOrder = React.useMemo(() => {
    if (!order) return false;
    return Boolean(order.tpvTransaction) || order.metadata?.isTPVSale === true;
  }, [order]);

  // Get the margin to apply: 70% for TPV, client margin for professional
  const marginToApply = React.useMemo(() => {
    if (isTPVOrder) {
      return 70; // TPV always uses 70% margin
    }
    // For professional orders, use the client margin stored in order metadata
    return parseFloat(order?.metadata?.clientMargin) || 0;
  }, [isTPVOrder, order]);

  // Helper function to apply margin to a price
  const applyMargin = React.useCallback((basePrice) => {
    return basePrice * (1 + marginToApply / 100);
  }, [marginToApply]);

  const [formData, setFormData] = React.useState({
    width: lineData?.width || '',
    height: lineData?.height || '',
    quantity: lineData?.quantity || 1,
    notes: lineData?.notes || '',
    measurementType: lineData?.measurementType || order?.metadata?.measurementType || 'EXT',
    isPriceManual: lineData?.isPriceManual || false,
    manualPrice: ''
  });

  // For add mode: selected line type and product
  const [newLineType, setNewLineType] = React.useState('trasera1');
  const [selectedProductId, setSelectedProductId] = React.useState('');

  // Track component changes - will be updated once products are loaded
  const [modifiedComponents, setModifiedComponents] = React.useState(
    (lineData?.components || []).map(comp => ({
      ...comp,
      productId: comp.productId || comp.product?.id,
      width: parseFloat(comp.width || lineData.width || 0),
      height: parseFloat(comp.height || lineData.height || 0)
    }))
  );

  // Fetch all products grouped by category
  const { data: rawProducts } = useQuery({
    queryKey: ['products'],
    queryFn: () => productService.getAll(),
  });

  // Normalize products to ensure pricePerUnit and category fields
  const allProducts = React.useMemo(() => {
    if (!rawProducts) return null;
    const normalized = rawProducts.map(p => ({
      ...p,
      pricePerUnit: p.pricePerUnit || p.basePrice || 0,
      // Prioritize categoryName from API, fallback to category or 'other'
      category: p.categoryName || p.category || 'other'
    }));
    console.log('Normalized products:', normalized.slice(0, 3));
    console.log('Unique categories:', [...new Set(normalized.map(p => p.category))]);
    return normalized;
  }, [rawProducts]);

  // Group products by category for easier selection
  const productsByCategory = React.useMemo(() => {
    if (!allProducts) return {};

    const grouped = allProducts.reduce((acc, product) => {
      const category = product.category || 'other';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(product);
      return acc;
    }, {});
    console.log('Products by category:', Object.keys(grouped), grouped);
    return grouped;
  }, [allProducts]);

  // When products load, update unitPrice from catalog but keep stored totalPrice
  // (recalculating totalPrice here causes rounding mismatches with OrderDetail)
  React.useEffect(() => {
    if (!allProducts || allProducts.length === 0) return;

    setModifiedComponents(prev => prev.map(comp => {
      const currentProduct = allProducts.find(p => p.id === comp.productId);
      if (!currentProduct) return comp;

      const basePrice = parseFloat(currentProduct.basePrice || currentProduct.pricePerUnit || 0);

      return {
        ...comp,
        unitPrice: basePrice
      };
    }));
  }, [allProducts]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleComponentProductChange = (componentIndex, newProductId) => {
    const newProduct = allProducts?.find(p => p.id === newProductId);
    if (!newProduct) return;

    const productPrice = parseFloat(newProduct.basePrice || newProduct.pricePerUnit || 0);

    setModifiedComponents(prev => {
      const updated = [...prev];
      const component = updated[componentIndex];

      // Recalculate price based on component type and dimensions
      let newPrice = 0;

      if (component.lineType === 'moldura') {
        // Linear meters calculation (minimum 20x20 for pricing)
        const pW = Math.max(parseFloat(formData.width), 20);
        const pH = Math.max(parseFloat(formData.height), 20);
        const linearMeters = ((pW + pH) * 2) / 100;
        newPrice = applyMargin(linearMeters * productPrice);
      } else if (['passepartout1', 'passepartout2', 'passepartout3', 'cristal1', 'cristal2', 'trasera1', 'trasera2', 'trasera3'].includes(component.lineType)) {
        // Square meters calculation (minimum 20x20 for pricing)
        const pW = Math.max(parseFloat(formData.width), 20);
        const pH = Math.max(parseFloat(formData.height), 20);
        const squareMeters = (pW * pH) / 10000;
        newPrice = applyMargin(squareMeters * productPrice);
      } else {
        // Accessories - fixed price
        newPrice = applyMargin(productPrice);
      }

      updated[componentIndex] = {
        ...component,
        productId: newProductId,
        product: newProduct,
        unitPrice: productPrice,
        totalPrice: newPrice
      };

      return updated;
    });
  };

  const handlePassepartoutWidthChange = (componentIndex, newWidth) => {
    setModifiedComponents(prev => {
      const updated = [...prev];
      const component = updated[componentIndex];

      // Update metadata with new passepartout width
      updated[componentIndex] = {
        ...component,
        metadata: {
          ...component.metadata,
          passepartoutWidth: newWidth
        },
        passepartoutWidth: newWidth
      };

      return updated;
    });
  };

  const handleDimensionChange = (field, value) => {
    handleChange(field, value);

    // Recalculate all component prices when dimensions change
    if ((field === 'width' || field === 'height') && value) {
      const inputWidth = field === 'width' ? parseFloat(value) : parseFloat(formData.width);
      const inputHeight = field === 'height' ? parseFloat(value) : parseFloat(formData.height);

      if (inputWidth && inputHeight) {
        const measurementType = formData.measurementType || 'EXT';

        setModifiedComponents(prev => {
          let currentWidth = inputWidth;
          let currentHeight = inputHeight;

          // Sort components by processing order
          const sorted = [...prev].sort((a, b) => {
            const order = measurementType === 'INT'
              ? ['passepartout', 'cristal', 'trasera', 'moldura', 'accesorio']
              : ['moldura', 'passepartout', 'cristal', 'trasera', 'accesorio'];

            const getTypeOrder = (type) => {
              if (type.startsWith('passepartout')) return order.indexOf('passepartout');
              if (type.startsWith('cristal')) return order.indexOf('cristal');
              if (type.startsWith('trasera')) return order.indexOf('trasera');
              if (type === 'moldura') return order.indexOf('moldura');
              if (type.startsWith('accesorio')) return order.indexOf('accesorio');
              return 999;
            };

            return getTypeOrder(a.lineType) - getTypeOrder(b.lineType);
          });

          return sorted.map((component, index) => {
            let newPrice = 0;
            let componentWidth = currentWidth;
            let componentHeight = currentHeight;

            if (component.lineType === 'moldura') {
              // Moldura uses current dimensions (minimum 20x20 for pricing)
              const pW = Math.max(currentWidth, 20);
              const pH = Math.max(currentHeight, 20);
              const linearMeters = ((pW + pH) * 2) / 100;
              newPrice = applyMargin(linearMeters * component.unitPrice);

              // After moldura, subtract frame width (if EXT)
              if (measurementType === 'EXT') {
                const frameWidth = parseFloat(component.product?.metadata?.width || component.product?.volume || 5);
                currentWidth -= (2 * frameWidth);
                currentHeight -= (2 * frameWidth);
              }

              console.log(`Moldura: ${linearMeters.toFixed(4)}m × ${component.unitPrice} + ${marginToApply}% margin = ${newPrice.toFixed(2)}`);
            } else if (component.lineType.startsWith('passepartout')) {
              // Passepartout uses current dimensions (minimum 20x20 for pricing)
              const pW = Math.max(currentWidth, 20);
              const pH = Math.max(currentHeight, 20);
              const squareMeters = (pW * pH) / 10000;
              newPrice = applyMargin(squareMeters * component.unitPrice);

              // After passepartout, adjust dimensions
              const ppWidth = parseFloat(component.metadata?.passepartoutWidth ?? component.passepartoutWidth ?? 5);
              if (measurementType === 'INT') {
                // INT: passepartout adds to dimensions
                currentWidth += (2 * ppWidth);
                currentHeight += (2 * ppWidth);
              } else {
                // EXT: passepartout subtracts from dimensions
                currentWidth -= (2 * ppWidth);
                currentHeight -= (2 * ppWidth);
              }

              console.log(`${component.lineType}: ${squareMeters.toFixed(4)}m² × ${component.unitPrice} + ${marginToApply}% margin = ${newPrice.toFixed(2)}, ppWidth: ${ppWidth}cm`);
            } else if (component.lineType.startsWith('cristal') || component.lineType.startsWith('trasera')) {
              // Cristal/Trasera use current dimensions (minimum 20x20 for pricing)
              const pW = Math.max(currentWidth, 20);
              const pH = Math.max(currentHeight, 20);
              const squareMeters = (pW * pH) / 10000;
              newPrice = applyMargin(squareMeters * component.unitPrice);

              console.log(`${component.lineType}: ${squareMeters.toFixed(4)}m² × ${component.unitPrice} + ${marginToApply}% margin = ${newPrice.toFixed(2)}`);
            } else if (component.lineType.startsWith('accesorio')) {
              // Accessories have fixed price
              newPrice = applyMargin(component.unitPrice);
            }

            return {
              ...component,
              width: componentWidth,
              height: componentHeight,
              totalPrice: newPrice
            };
          });
        });
      }
    }
  };

  // State for adding new components within edit mode
  const [pendingNewComponents, setPendingNewComponents] = React.useState([]);
  const [showAddComponent, setShowAddComponent] = React.useState(false);

  // Add a new component to the pending list
  const handleAddNewComponent = () => {
    if (!selectedProductId) return;

    const product = allProducts?.find(p => p.id === selectedProductId);
    if (!product) return;

    const productPrice = parseFloat(product.basePrice || product.pricePerUnit || 0);
    const width = parseFloat(formData.width) || 0;
    const height = parseFloat(formData.height) || 0;

    // Calculate price based on line type (applying margin for TPV/professional)
    let totalPrice = applyMargin(productPrice);
    if (newLineType === 'moldura' && width && height) {
      const pW = Math.max(width, 20);
      const pH = Math.max(height, 20);
      const linearMeters = ((pW + pH) * 2) / 100;
      totalPrice = applyMargin(linearMeters * productPrice);
    } else if (!newLineType.startsWith('accesorio') && width && height) {
      const pW = Math.max(width, 20);
      const pH = Math.max(height, 20);
      const squareMeters = (pW * pH) / 10000;
      totalPrice = applyMargin(squareMeters * productPrice);
    }

    const newComponent = {
      id: `new-${Date.now()}`,
      isNew: true,
      lineType: newLineType,
      productId: selectedProductId,
      product: product,
      width: width,
      height: height,
      unitPrice: productPrice,
      totalPrice: totalPrice,
      metadata: {}
    };

    setPendingNewComponents(prev => [...prev, newComponent]);
    setSelectedProductId('');
    setShowAddComponent(false);
  };

  // Remove a pending new component
  const handleRemovePendingComponent = (index) => {
    setPendingNewComponents(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    if (isAddMode) {
      // Add mode: pass data for creating new line
      onSave({
        mode: 'add',
        lineType: newLineType,
        productId: selectedProductId,
        width: formData.width,
        height: formData.height,
        quantity: formData.quantity,
        notes: formData.notes,
        articleFingerprint
      });
    } else {
      // Edit mode: pass updated data including new components to create
      onSave({
        ...lineData,
        ...formData,
        isPriceManual: formData.isPriceManual,
        manualPrice: formData.isPriceManual ? parseFloat(formData.manualPrice) : null,
        components: modifiedComponents,
        newComponents: pendingNewComponents // New components to add to this article
      });
    }
  };

  // Get available line types for add mode
  const lineTypeOptions = [
    { value: 'moldura', label: 'Moldura' },
    { value: 'passepartout1', label: 'Passepartout 1' },
    { value: 'passepartout2', label: 'Passepartout 2' },
    { value: 'passepartout3', label: 'Passepartout 3' },
    { value: 'cristal1', label: 'Cristal 1' },
    { value: 'cristal2', label: 'Cristal 2' },
    { value: 'trasera1', label: 'Trasera 1' },
    { value: 'trasera2', label: 'Trasera 2' },
    { value: 'trasera3', label: 'Trasera 3' },
    { value: 'accesorio1', label: 'Accesorio 1' },
    { value: 'accesorio2', label: 'Accesorio 2' },
    { value: 'accesorio3', label: 'Accesorio 3' },
    { value: 'accesorio4', label: 'Accesorio 4' }
  ];

  // Get filtered products for selected line type in add mode
  const getProductsForLineType = (lineType) => {
    if (!allProducts) return [];
    const categoryMap = {
      'moldura': 'Molduras',
      'passepartout': 'Passepartous',
      'cristal': 'Cristal',
      'trasera': 'Traseras',
      'accesorio': 'Accesorios'
    };
    const baseType = lineType.replace(/[0-9]/g, '');
    const category = categoryMap[baseType] || 'other';
    const products = productsByCategory[category] || [];
    console.log(`getProductsForLineType: lineType=${lineType}, baseType=${baseType}, category=${category}, found=${products.length} products`);
    return products;
  };

  const productsForNewLine = getProductsForLineType(newLineType);

  // Helper function to round to 5 cents (for TPV display)
  const roundToFiveCents = (number) => Math.ceil(number * 20) / 20;

  // Include both existing and pending new components in total
  const allComponentsForTotal = [...modifiedComponents, ...pendingNewComponents];
  const totalLineBase = allComponentsForTotal.reduce((sum, comp) => {
    return sum + parseFloat(comp.totalPrice || 0);
  }, 0);
  const totalLineQuantity = allComponentsForTotal.length > 0
    ? Math.max(...allComponentsForTotal.map(comp => parseFloat(comp.quantity || 1))) || 1
    : 1;
  const unitBase = totalLineBase / totalLineQuantity;
  const totalLine = showPriceWithVAT
    ? roundToFiveCents(unitBase * 1.21) * totalLineQuantity
    : totalLineBase;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{isAddMode ? 'Añadir Elemento' : 'Editar Línea de Pedido'}</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <EditForm>
          {isAddMode && (
            <AddComponentSection>
              <FormGroup>
                <Label>Tipo de Elemento</Label>
                <Select
                  value={newLineType}
                  onChange={(e) => {
                    setNewLineType(e.target.value);
                    setSelectedProductId('');
                  }}
                >
                  {lineTypeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Producto ({productsForNewLine.length} disponibles)</Label>
                <Select
                  value={selectedProductId}
                  onChange={(e) => setSelectedProductId(e.target.value)}
                >
                  <option value="">
                    {!allProducts ? 'Cargando productos...' :
                     productsForNewLine.length === 0 ? 'No hay productos en esta categoría' :
                     'Seleccionar producto...'}
                  </option>
                  {productsForNewLine.map(product => (
                    <option key={product.id} value={product.id}>
                      {product.name} - {formatCurrency(product.basePrice || product.pricePerUnit || 0)}/{product.unit || 'ud'}
                    </option>
                  ))}
                </Select>
              </FormGroup>
            </AddComponentSection>
          )}

          <FormGroup>
            <Label>Dimensiones</Label>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <Input
                type="number"
                placeholder="Ancho"
                value={formData.width}
                onChange={(e) => handleDimensionChange('width', e.target.value)}
                style={{ width: '100px' }}
              />
              <span>×</span>
              <Input
                type="number"
                placeholder="Alto"
                value={formData.height}
                onChange={(e) => handleDimensionChange('height', e.target.value)}
                style={{ width: '100px' }}
              />
              <span>cm</span>
              <span style={{
                marginLeft: '1rem',
                padding: '0.4rem 0.8rem',
                background: formData.measurementType === 'INT' ? '#e3f2fd' : '#fff3e0',
                border: `1px solid ${formData.measurementType === 'INT' ? '#2196f3' : '#ff9800'}`,
                borderRadius: '4px',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: formData.measurementType === 'INT' ? '#1976d2' : '#f57c00'
              }}>
                {formData.measurementType === 'INT' ? '📐 Interior' : '📏 Exterior'}
              </span>
            </div>
          </FormGroup>

          <FormGroup>
            <Label>Cantidad</Label>
            <Input
              type="number"
              min="1"
              value={formData.quantity}
              onChange={(e) => handleChange('quantity', parseInt(e.target.value) || 1)}
              style={{ width: '100px' }}
            />
          </FormGroup>

          <FormGroup>
            <Label>Notas</Label>
            <TextArea
              placeholder="Añadir notas sobre esta línea..."
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
            />
          </FormGroup>

          {!isAddMode && (
            <FormGroup>
              <Label>Precio</Label>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    checked={formData.isPriceManual}
                    onChange={(e) => handleChange('isPriceManual', e.target.checked)}
                  />
                  Precio manual
                </CheckboxLabel>
                {formData.isPriceManual && (
                  <PriceInput
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    className="manual"
                    value={formData.manualPrice}
                    onChange={(e) => handleChange('manualPrice', e.target.value)}
                  />
                )}
              </div>
              {formData.isPriceManual && (
                <span style={{ fontSize: '0.8rem', color: '#ff9800', marginTop: '0.25rem' }}>
                  ⚠️ El precio manual no se recalculará automáticamente
                </span>
              )}
            </FormGroup>
          )}

          {!isAddMode && (
          <FormGroup>
            <Label>Componentes ({modifiedComponents.length + pendingNewComponents.length})</Label>
            <ComponentsList>
              {/* Existing components */}
              {modifiedComponents.map((component, index) => {
                const price = parseFloat(component.totalPrice || 0);
                const displayPrice = showPriceWithVAT ? price * 1.21 : price;

                // Map lineType to category name (exact match from DB)
                let categoryForFilter = 'other';
                const lineType = component.lineType || '';
                if (lineType === 'moldura') {
                  categoryForFilter = 'Molduras';
                } else if (lineType.startsWith('passepartout')) {
                  categoryForFilter = 'Passepartous';
                } else if (lineType.startsWith('cristal')) {
                  categoryForFilter = 'Cristal';
                } else if (lineType.startsWith('trasera')) {
                  categoryForFilter = 'Traseras';
                } else if (lineType.startsWith('accesorio')) {
                  categoryForFilter = 'Accesorios';
                }

                const availableProducts = productsByCategory[categoryForFilter] || [];
                const isPassepartout = lineType.startsWith('passepartout');
                const passepartoutWidth = component.metadata?.passepartoutWidth ?? component.passepartoutWidth ?? 5;

                return (
                  <ComponentItem key={component.id || index}>
                    <ComponentInfo>
                      <ComponentName>{component.product?.name || 'Componente'}</ComponentName>
                      <ComponentType>{component.lineType || 'N/A'}</ComponentType>
                      <Select
                        value={component.productId}
                        onChange={(e) => handleComponentProductChange(index, e.target.value)}
                      >
                        {availableProducts.length === 0 ? (
                          <option value="">No hay productos disponibles</option>
                        ) : (
                          availableProducts.map(product => (
                            <option key={product.id} value={product.id}>
                              {product.name} - {formatCurrency(product.basePrice || product.pricePerUnit || 0)}/{product.unit || 'ud'}
                            </option>
                          ))
                        )}
                      </Select>
                      {isPassepartout && (
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem' }}>
                          <label style={{ fontSize: '0.875rem', color: '#666' }}>Ancho:</label>
                          <Input
                            type="number"
                            min="0"
                            step="0.5"
                            value={passepartoutWidth}
                            onChange={(e) => handlePassepartoutWidthChange(index, parseFloat(e.target.value))}
                            style={{ width: '80px' }}
                          />
                          <span style={{ fontSize: '0.875rem', color: '#666' }}>cm</span>
                        </div>
                      )}
                    </ComponentInfo>
                    <ComponentActions>
                      <ComponentPrice>{formatCurrency(displayPrice)}</ComponentPrice>
                    </ComponentActions>
                  </ComponentItem>
                );
              })}

              {/* Pending new components (to be added) */}
              {pendingNewComponents.map((component, index) => {
                const price = parseFloat(component.totalPrice || 0);
                const displayPrice = showPriceWithVAT ? price * 1.21 : price;

                return (
                  <ComponentItem key={component.id} style={{ borderLeftColor: '#4caf50', backgroundColor: '#f1f8e9' }}>
                    <ComponentInfo>
                      <ComponentName>
                        <span style={{ color: '#4caf50', marginRight: '0.5rem' }}>+ NUEVO</span>
                        {component.product?.name || 'Componente'}
                      </ComponentName>
                      <ComponentType>{component.lineType}</ComponentType>
                    </ComponentInfo>
                    <ComponentActions>
                      <ComponentPrice>{formatCurrency(displayPrice)}</ComponentPrice>
                      <button
                        type="button"
                        onClick={() => handleRemovePendingComponent(index)}
                        style={{
                          background: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '0.25rem 0.5rem',
                          cursor: 'pointer',
                          fontSize: '0.75rem'
                        }}
                      >
                        Quitar
                      </button>
                    </ComponentActions>
                  </ComponentItem>
                );
              })}
            </ComponentsList>

            {/* Add new component section */}
            {!showAddComponent ? (
              <button
                type="button"
                onClick={() => setShowAddComponent(true)}
                style={{
                  marginTop: '1rem',
                  padding: '0.5rem 1rem',
                  backgroundColor: '#4caf50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                + Añadir componente
              </button>
            ) : (
              <AddComponentSection style={{ marginTop: '1rem' }}>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                  <Select
                    value={newLineType}
                    onChange={(e) => {
                      setNewLineType(e.target.value);
                      setSelectedProductId('');
                    }}
                    style={{ flex: 1 }}
                  >
                    {lineTypeOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </Select>
                  <Select
                    value={selectedProductId}
                    onChange={(e) => setSelectedProductId(e.target.value)}
                    style={{ flex: 2 }}
                  >
                    <option value="">
                      {productsForNewLine.length === 0 ? 'No hay productos' : 'Seleccionar producto...'}
                    </option>
                    {productsForNewLine.map(product => (
                      <option key={product.id} value={product.id}>
                        {product.name} - {formatCurrency(product.basePrice || product.pricePerUnit || 0)}
                      </option>
                    ))}
                  </Select>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    type="button"
                    onClick={handleAddNewComponent}
                    disabled={!selectedProductId}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: selectedProductId ? '#4caf50' : '#ccc',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: selectedProductId ? 'pointer' : 'not-allowed'
                    }}
                  >
                    Añadir
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddComponent(false)}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancelar
                  </button>
                </div>
              </AddComponentSection>
            )}
          </FormGroup>
          )}

          {!isAddMode && (
          <SummarySection>
            <SummaryRow>
              <span>Cantidad:</span>
              <span>{formData.quantity}</span>
            </SummaryRow>
            <SummaryRow>
              <span>Precio unitario (suma componentes):</span>
              <span>{formatCurrency(totalLine)}</span>
            </SummaryRow>
            <SummaryRow>
              <span>Total {showPriceWithVAT ? '(IVA incluido)' : '(sin IVA)'}:</span>
              <span>{formatCurrency(totalLine * formData.quantity)}</span>
            </SummaryRow>
          </SummarySection>
          )}
        </EditForm>

        <ModalActions>
          <CancelButton onClick={onClose}>Cancelar</CancelButton>
          <SaveButton
            onClick={handleSave}
            disabled={isAddMode && !selectedProductId}
          >
            {isAddMode ? 'Añadir' : 'Guardar Cambios'}
          </SaveButton>
        </ModalActions>
      </ModalContent>
    </ModalOverlay>
  );
};

export default EditLineModal;
